/* eslint-disable no-magic-numbers */
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { ITEM, SNACKBAR } from '@utils/constants';
import { SnackbarConfig } from './snackbar.config';

@Component({
  selector: 'app-snackbar',
  standalone: true,
  imports: [NgClass],
  templateUrl: './snackbar.component.html',
  styleUrl: './snackbar.component.scss',
  animations: [
    trigger('slideInOut', [
      state('in', style({ transform: 'translateY(0)', opacity: 1 })),
      state('out', style({ transform: 'translateY(100%)', opacity: 0 })),
      transition('void => in', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('300ms ease-out')
      ]),
      transition('in => out', [
        animate('300ms ease-in')
      ])
    ])
  ]
})
export class SnackbarComponent {
  isVisible = signal<boolean>(false);
  config = signal<SnackbarConfig>({
    message: '',
    type: 'info',
    duration: 5000,
    countdown: 0,
    showCountdown: false
  });
  countdown = signal<number>(ITEM.DEFAULT_VALUE);
  
  private countdownInterval?: number;
  private autoHideTimeout?: number;

  ngOnInit() {
    // Component initialization if needed
  }

  ngOnDestroy() {
    this.clearTimers();
  }

  // Computed styles based on snackbar type
  snackbarClasses = computed(() => {
    const baseClasses = 'text-white';
    const typeClasses = {
      error: 'bg-red-600',
      warning: 'bg-yellow-600',
      success: 'bg-green-600',
      info: 'bg-blue-600'
    };
    return `${baseClasses} ${typeClasses[this.config().type]}`;
  });

  countdownTextClasses = computed(() => {
    const typeClasses = {
      error: 'text-red-100',
      warning: 'text-yellow-100',
      success: 'text-green-100',
      info: 'text-blue-100'
    };
    return typeClasses[this.config().type];
  });

  closeButtonClasses = computed(() => {
    const typeClasses = {
      error: 'text-red-200 hover:text-white',
      warning: 'text-yellow-200 hover:text-white',
      success: 'text-green-200 hover:text-white',
      info: 'text-blue-200 hover:text-white'
    };
    return typeClasses[this.config().type];
  });

  show(config: SnackbarConfig) {
    this.config.set({
      ...config,
      duration: config.duration ?? SNACKBAR.DEFAULT_DURATION,
      showCountdown: config.showCountdown ?? false
    });
    
    this.isVisible.set(true);
    
    if (config.countdown && config.countdown > ITEM.DEFAULT_VALUE) {
      this.countdown.set(config.countdown);
      this.startCountdown();
    } else if (config.duration && config.duration > ITEM.DEFAULT_VALUE) {
      this.autoHideTimeout = window.setTimeout(() => {
        this.close();
      }, config.duration);
    }
  }

  close() {
    this.isVisible.set(false);
    this.clearTimers();
  }

  private startCountdown() {
    this.countdownInterval = window.setInterval(() => {
      const current = this.countdown();
      if (current > ITEM.DEFAULT_VALUE) {
        this.countdown.set(current - ITEM.SINGLE_EMISSION);
      } else {
        this.clearTimers();
        // Auto hide after countdown completes
        setTimeout(() => {
          this.close();
        }, 2000);
      }
    }, 1000);
  }

  private clearTimers() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = undefined;
    }
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
